import { useState, useCallback } from 'react';

/**
 * State and handlers for managing CRUD dialogs (create/edit form and delete confirmation)
 *
 * @template T - The entity type being managed
 *
 * @example
 * const {
 *   formDialogOpen,
 *   editingEntity,
 *   deleteDialogOpen,
 *   deletingEntityId,
 *   openCreate,
 *   openEdit,
 *   openDelete,
 *   closeFormDialog,
 *   closeDeleteDialog
 * } = useCrudDialogState<Department>();
 */
export function useCrudDialogState<T extends { _id: string }>() {
  // Form dialog state. `opening` counts the times the form dialog was opened,
  // so closing and reopening the same entity is a new opening.
  const [formDialog, setFormDialog] = useState<{ open: boolean; entity?: T; opening: number }>({
    open: false,
    opening: 0
  });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntityId, setDeletingEntityId] = useState<string | null>(null);

  // Handlers for opening dialogs
  const openCreate = useCallback(() => {
    setFormDialog(({ opening }) => ({ open: true, opening: opening + 1 }));
  }, []);

  const openEdit = useCallback((entity: T) => {
    setFormDialog(({ opening }) => ({ open: true, entity, opening: opening + 1 }));
  }, []);

  const openDelete = useCallback((id: string) => {
    setDeletingEntityId(id);
    setDeleteDialogOpen(true);
  }, []);

  // Handlers for closing dialogs
  const closeFormDialog = useCallback(() => {
    setFormDialog(({ opening }) => ({ open: false, opening }));
  }, []);

  /** Closes the form dialog only if it is still the opening given. */
  const closeFormDialogFrom = useCallback((opening: number) => {
    setFormDialog((current) => (current.opening === opening ? { open: false, opening } : current));
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeletingEntityId(null);
  }, []);

  return {
    // Form dialog state
    formDialogOpen: formDialog.open,
    editingEntity: formDialog.entity,
    formDialogOpening: formDialog.opening,

    // Delete dialog state
    deleteDialogOpen,
    deletingEntityId,

    // Form dialog handlers
    openCreate,
    openEdit,
    closeFormDialog,
    closeFormDialogFrom,

    // Delete dialog handlers
    openDelete,
    closeDeleteDialog,
    setDeleteDialogOpen
  };
}
